<template>
  <application-layout>
    <v-container fluid>
      <v-stepper v-model="stepperStep" style="height: 87vh">
        <v-stepper-header>
          <v-stepper-step
            :color="stepperStep === 1 ? 'secondary' : 'success'"
            :complete="stepperStep > 1"
            step="1"
            :class="stepperStep === 1 ? 'title' : 'subheading'"
            data-testid="step-one-header"
          >
            Unit Select
          </v-stepper-step>
          <v-divider :class="stepperStep > 1 ? 'success' : null" />
          <v-stepper-step
            :color="stepperStep === 2 ? 'secondary' : 'success'"
            :complete="stepperStep > 2"
            step="2"
            :class="stepperStep === 2 ? 'title' : 'subheading'"
            data-testid="step-two-header"
          >
            Payment Date
          </v-stepper-step>
          <v-divider :class="stepperStep > 2 ? 'success' : null" />
          <v-stepper-step
            :color="stepperStep === 3 ? 'secondary' : 'success'"
            :complete="stepperStep > 3"
            step="3"
            :class="stepperStep === 3 ? 'title' : 'subheading'"
            data-testid="step-three-header"
          >
            Payment Review
          </v-stepper-step>
          <v-divider :class="stepperStep > 3 ? 'success' : null" />
          <v-stepper-step
            :color="stepperStep === 4 ? 'secondary' : 'success'"
            :complete="stepperStep > 4"
            step="4"
            :class="stepperStep === 4 ? 'title' : 'subheading'"
            data-testid="step-four-header"
          >
            Payment Invoice
          </v-stepper-step>
        </v-stepper-header>
        <v-stepper-items :class="darkMode ? 'secondary' : 'grey lighten-4'">
          <v-layout column style="height: 70.25vh; overflow: auto">
            <v-layout>
              <v-flex :xs8="stepperStep < 4">
                <v-stepper-content step="1">
                  <promise-unit-select />
                </v-stepper-content>
                <v-stepper-content step="2">
                  <promise-date />
                </v-stepper-content>
                <v-stepper-content step="3">
                  <promise-review />
                </v-stepper-content>
                <v-stepper-content step="4">
                  <promise-invoice />
                </v-stepper-content>
              </v-flex>
              <v-flex xs4 v-show="stepperStep < 4">
                <v-container>
                  <plan-side-total />
                  <div class="px-3 py-4">
                    <div class="text-h6">Promise Months:</div>
                    <v-radio-group v-model="promiseMonths" row>
                      <v-radio
                        data-testid="promise-current-month-radio"
                        class="promise-current-month-radio"
                        label="Current Month"
                        color="primary"
                        value="1"
                      />
                      <v-radio
                        data-testid="promise-current-next-month-radio"
                        class="promise-current-next-month-radio"
                        label="Current + Next Month"
                        color="info"
                        value="2"
                      />
                    </v-radio-group>
                  </div>
                </v-container>
              </v-flex>
            </v-layout>
          </v-layout>
          <v-divider />
          <v-flex>
            <stepper-nav-buttons
              :loading="loading"
              @backToTenant="handleBackToTenant"
              @nextStep="handleNextStep"
              @priorStep="handlePriorStep"
              @openDisposition="handleDisposition"
              @submit="handleCreateScheduledPromise"
            />
          </v-flex>
        </v-stepper-items>
      </v-stepper>
      <disposition
        :dispositionOpen="dispositionOpen"
        @collapse="dispositionOpen = false"
        isAccountsReceivable
      />
    </v-container>
  </application-layout>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import ApplicationLayout from "@/layouts/ApplicationLayout";
import Disposition from "@/components/Disposition";
import PlanSideTotal from "@/modules/accounts-receivable/components/PlanSideTotal";
import PromiseDate from "@/modules/accounts-receivable/views/promise-to-pay/steps/PromiseDate";
import PromiseInvoice from "@/modules/accounts-receivable/views/promise-to-pay/steps/PromiseInvoice";
import PromiseReview from "@/modules/accounts-receivable/views/promise-to-pay/steps/PromiseReview";
import PromiseUnitSelect from "@/modules/accounts-receivable/views/promise-to-pay/steps/PromiseUnitSelect";
import StepperNavButtons from "@/modules/accounts-receivable/components/StepperNavButtons";
import router from "@/router";

export default {
  name: "PromiseToPay",

  components: {
    ApplicationLayout,
    Disposition,
    StepperNavButtons,
    PromiseUnitSelect,
    PromiseDate,
    PromiseReview,
    PromiseInvoice,
    PlanSideTotal
  },

  data() {
    return {
      dispositionOpen: false,
      loading: false
    };
  },

  methods: {
    async handleBackToTenant() {
      await this.$store.commit("setCurrentStep", 1);
      await this.$store.commit("resetAccountsReceivableState");
      await this.getAllPaymentPlans();
      await this.getAllPromiseToPays();
      await router.push({
        name: "arTenant",
        params: {
          facilityId: this.$route.params.facilityId,
          tenantId: this.$route.params.tenantId
        }
      }).catch(() => {});
    },

    handleNextStep() {
      this.$store.commit("setCurrentStep", (this.currentStep + 1));
    },

    handlePriorStep() {
      this.$store.commit("setCurrentStep", (this.currentStep - 1));
    },

    handleDisposition() {
      this.dispositionOpen = true;
    },

    handleCreateScheduledPromise() {
      this.loading = true;
      let payload = {
        payment_date: this.promiseDate,
        payment_amount: this.selectedPromiseToPay.total_due.toFixed(2),
        status: "pending",
        tenant_id: this.info.external_id,
        ledger_id: this.selectedPromiseToPay.ledger_id,
        facility_id: this.$route.params.facilityId,
        unit_id: this.selectedPromiseToPay.unit_id,
        first_name: this.info.first_name,
        last_name: this.info.last_name,
        email: this.info.email,
        phone: this.info.phone_number
      };

      this.$store.dispatch("createScheduledPromisePayment", payload)
        .then(() => {
          this.$store.dispatch("handleSuccess", "Promise-to-Pay successfully created!");
          this.handleNextStep();
        })
        .catch((err) => {
          this.loading = false;
          this.$store.dispatch("handleError", err);
        });
    },

    getAllPaymentPlans() {
      let payload = {
        facilityId: this.$route.params.facilityId,
        tenantId: this.$route.params.tenantId
      };

      this.$store.dispatch("getAllPaymentPlansForTenant", payload)
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    },

    getAllPromiseToPays() {
      let payload = {
        facilityId: this.$route.params.facilityId,
        tenantId: this.$route.params.tenantId
      };

      this.$store.dispatch("getAllPromisesToPayForTenant", payload)
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    }
  },

  computed: {
    ...mapState({
      currentStep: state => state.application.currentStep,
      darkMode: state => state.application.settings.darkTheme,
      info: state => state.tenant.info,
      ledgers: state => state.tenant.ledgers,
      promiseCart: state => state.accountsReceivable.promiseCart,
      promiseDate: state => state.accountsReceivable.promiseDate,
      promiseMonthsChoice: state => state.accountsReceivable.promiseMonths
    }),

    ...mapGetters({
      selectedPromiseToPay: "selectedPromiseToPay"
    }),

    stepperStep: {
      get() {
        return this.currentStep;
      },
      set(newVal) {
        this.$store.commit("setCurrentStep", newVal);
      }
    },

    promiseMonths: {
      get() {
        return this.promiseMonthsChoice;
      },
      set(months) {
        this.$store.commit("setPromiseMonths", months);
      }
    }
  }
};
</script>

<style scoped>

</style>
